import type { TFunction } from 'i18next';
import { toIntlLocale } from 'src/functions/date.functions';
import { LEAD_AGENT_SELECTABLE_TIERS } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import { ePlanCode } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import {
  ALERT_PRIORITY_ORDER,
  PAST_DUE_FINAL_ALERT_ID,
} from './AlertCenter.constants';
import { ALERT_REGISTRY } from './AlertRegistry';
import {
  tAlertDeclaration,
  tAlertPlacement,
  tAlertSnapshot,
  tLiveAlert,
} from './AlertCenter.types';

/**
 * `<alert>:<workspace>[:<period>]` — Q1 proposes this same shape server-side.
 *
 * The period segment is either an ISO date (`YYYY-MM-DD`, the subscription
 * period end) or `YYYY-MM-fallback` when no period end is available. The
 * `-fallback` marker is part of the contract, not an artifact: a bare
 * first-of-month (`2026-08-01`) could collide with a genuine period end on
 * that exact day, and the suffix rules that out by construction. A backend
 * that parses or validates this segment has to accept both forms.
 */
export function buildAlertKey(
  declaration: tAlertDeclaration,
  snapshot: tAlertSnapshot,
): string {
  return formatAlertKey(
    declaration.id,
    snapshot.workspaceId ?? 'unknown',
    declaration.period?.(snapshot) ?? null,
  );
}

/**
 * The key's only construction site. `selectAlerts` resolves the period once
 * and passes it to both the key and `tLiveAlert.occurrence`, so the two can
 * never disagree — carrying `occurrence` explicitly exists precisely so the
 * R13b guard does not judge a recomputed value. Today's only resolver is a
 * pure snapshot read, but a future one that touches a clock or a locale would
 * desync the two if each computed its own.
 */
function formatAlertKey(
  id: string,
  workspace: string,
  occurrence: string | null,
): string {
  return occurrence ? `${id}:${workspace}:${occurrence}` : `${id}:${workspace}`;
}

/**
 * R3b — a declaration whose contributing source is still loading is
 * "unresolved". It neither renders nor participates in suppression, because a
 * suppression decided on unknown state swaps alerts under the user.
 */
export function isUnresolved(
  declaration: tAlertDeclaration,
  snapshot: tAlertSnapshot,
): boolean {
  if (!declaration.dependsOn?.length) return false;
  return declaration.dependsOn.some((key) => {
    const source = snapshot[key] as { status?: string } | null | undefined;
    return (
      !!source && typeof source === 'object' && source.status === 'loading'
    );
  });
}

/**
 * RET-1957 — the ONE place a declaration's placement is read. A family whose
 * delivery surface follows its own state declares a resolver (the vencido
 * alert is in-page while fresh, `slot` once it decays); a fixed string is the
 * degenerate case. Resolved once per snapshot in `selectAlerts`, beside
 * prominence, so every consumer reads the plain value off `tLiveAlert` and no
 * surface filter has to know a function is possible.
 */
export function resolveAlertPlacement(
  declaration: tAlertDeclaration,
  snapshot: tAlertSnapshot,
): tAlertPlacement {
  return typeof declaration.placement === 'function'
    ? declaration.placement(snapshot)
    : declaration.placement;
}

/**
 * Resuelve `dismissible`, que puede venir como valor o como función del
 * snapshot — igual que `placement` arriba. Exportado por la misma razón: los
 * fixtures que construyen un aviso YA RESUELTO tienen que resolverlo por el
 * mismo camino que la selección, o mienten en cuanto una declaración pasa de
 * valor a función.
 */
export function resolveAlertDismissible(
  declaration: tAlertDeclaration,
  snapshot: tAlertSnapshot,
): boolean {
  return typeof declaration.dismissible === 'function'
    ? declaration.dismissible(snapshot)
    : declaration.dismissible;
}

/** R17a — an alert with a surface scope resolves only on those surfaces. */
function isOnSurface(
  declaration: tAlertDeclaration,
  snapshot: tAlertSnapshot,
): boolean {
  if (!declaration.surfaces?.length) return true;
  if (snapshot.surface === null) return false;
  return declaration.surfaces.includes(snapshot.surface);
}

/**
 * R3 — within an exclusivity group only the highest-priority member survives.
 * Ties inside a group fall back to declaration order, so the result is stable.
 */
export function applyExclusivity(
  declarations: tAlertDeclaration[],
): tAlertDeclaration[] {
  const winnerByGroup = new Map<string, tAlertDeclaration>();
  for (const declaration of declarations) {
    const group = declaration.exclusivityGroup;
    if (!group) continue;
    const incumbent = winnerByGroup.get(group);
    if (
      !incumbent ||
      ALERT_PRIORITY_ORDER[declaration.priority] <
        ALERT_PRIORITY_ORDER[incumbent.priority]
    ) {
      winnerByGroup.set(group, declaration);
    }
  }
  return declarations.filter((declaration) => {
    const group = declaration.exclusivityGroup;
    if (!group) return true;
    return winnerByGroup.get(group) === declaration;
  });
}

/**
 * R3a — cross-group suppression, with the co-mount invariant. A suppressor
 * only suppresses on surfaces it itself reaches: suppressing where the winner
 * does not render would leave the user with no alert at all.
 *
 * AN ACKNOWLEDGED SUPPRESSOR SUPPRESSES NOTHING (2026-09-02, adversarial
 * review). Suppression used to run before the acknowledgment filter — that
 * filter lives in the provider, downstream of this — so a P1 the owner had
 * already cleared went on hiding its victims. Concretely:
 * `la-transfer-pending` suppresses `la-usage-capped`, which is a P0 that
 * cannot itself be dismissed; acknowledging the invoice row laundered the P0
 * out of view, and only on the surfaces the P1 reaches, so the same workspace
 * saw the P0 in Chat and not on Inicio in the same second.
 *
 * Only the POWER TO SUPPRESS is withdrawn here, not the alert: the suppressor
 * stays in the returned list and the provider filters it as it always did.
 * That matters because `AlertDetailModal` judges liveness against the
 * unfiltered list — dropping it here would make an open detail read as
 * resolved the moment its row was acknowledged behind it.
 */
export function applySuppression(
  declarations: tAlertDeclaration[],
  snapshot: tAlertSnapshot,
  acknowledgedKeys: readonly string[] = [],
): tAlertDeclaration[] {
  const suppressed = new Set<string>();
  for (const declaration of declarations) {
    if (!declaration.suppresses?.length) continue;
    if (isUnresolved(declaration, snapshot)) continue;
    if (!isOnSurface(declaration, snapshot)) continue;
    if (acknowledgedKeys.length > 0) {
      const key = formatAlertKey(
        declaration.id,
        snapshot.workspaceId ?? 'unknown',
        declaration.period?.(snapshot) ?? null,
      );
      if (acknowledgedKeys.includes(key)) continue;
    }
    declaration.suppresses.forEach((id) => suppressed.add(id));
  }
  return declarations.filter((declaration) => !suppressed.has(declaration.id));
}

/** R4 — priority first, then declaration order. Stable across repeated calls. */
export function orderAlerts(
  declarations: tAlertDeclaration[],
): tAlertDeclaration[] {
  const indexOf = new Map(ALERT_REGISTRY.map((d, i) => [d.id, i]));
  return [...declarations].sort((a, b) => {
    const byPriority =
      ALERT_PRIORITY_ORDER[a.priority] - ALERT_PRIORITY_ORDER[b.priority];
    if (byPriority !== 0) return byPriority;
    return (indexOf.get(a.id) ?? 0) - (indexOf.get(b.id) ?? 0);
  });
}

/**
 * The full pipeline: derive, filter by role and surface, drop retired, collapse
 * exclusivity groups, apply cross-group suppression, then order.
 */
export function selectAlerts(
  snapshot: tAlertSnapshot,
  registry: tAlertDeclaration[] = ALERT_REGISTRY,
  /**
   * Keys the viewer has already cleared. Used ONLY to strip a suppressor's
   * power over its victims (see `applySuppression`); the acknowledged alert
   * itself is still returned, and the provider filters it downstream.
   */
  acknowledgedKeys: readonly string[] = [],
): tLiveAlert[] {
  const role = snapshot.role;
  if (!role) return [];

  const derived = registry.filter((declaration) => {
    if (!declaration.roles.includes(role)) return false;
    if (!isOnSurface(declaration, snapshot)) return false;
    if (isUnresolved(declaration, snapshot)) return false;
    let live = false;
    try {
      live = declaration.derive(snapshot);
    } catch {
      // A source that blew up is resolved-unknown, not live (R3b).
      live = false;
    }
    if (!live) return false;
    // KTD16 — `retired` removes the alert from selection while its predicate
    // is still true, which is why it cannot be modelled as resolution.
    return declaration.prominence?.(snapshot) !== 'retired';
  });

  const ordered = orderAlerts(
    applySuppression(applyExclusivity(derived), snapshot, acknowledgedKeys),
  );

  return ordered.map((declaration) => {
    const occurrence = declaration.period?.(snapshot) ?? null;
    // Both derived values are read ONCE here (RET-1957). They are pure
    // snapshot reads today, but a surface that re-resolved placement on its
    // own could disagree with the one selection published — and "the alert is
    // in two places" is exactly what the one-surface rule forbids.
    const placement = resolveAlertPlacement(declaration, snapshot);
    const prominence = declaration.prominence?.(snapshot) ?? null;
    return {
      key: formatAlertKey(
        declaration.id,
        snapshot.workspaceId ?? 'unknown',
        occurrence,
      ),
      occurrence,
      id: declaration.id,
      priority: declaration.priority,
      placement,
      windowRank: declaration.windowRank ?? null,
      // Resuelto UNA vez aquí, como `placement` y `prominence`: si cada
      // superficie lo volviera a resolver podrían discrepar sobre si el mismo
      // aviso publicado se puede cerrar o no.
      dismissible: resolveAlertDismissible(declaration, snapshot),
      prominence,
      copy: declaration.copy(snapshot, role),
      declaration,
    };
  });
}

export type tWideArbitration = {
  /** The single P0 that owns the wide strip, or null when no critical is live. */
  winner: tLiveAlert | null;
  /** The losing criticals, shortest window first — the bell's "waiting" list. */
  waiting: tLiveAlert[];
};

/** Arbitration produces the same object for every empty input, so consumers
 *  memoized on the result do not re-render when nothing competes. */
const EMPTY_WIDE_ARBITRATION: tWideArbitration = { winner: null, waiting: [] };

/**
 * D4 — the master rule: ONE wide critical per viewer. The input is the
 * selected set, already filtered by role (an asesor never receives
 * `la-usage-capped`, so their arbitration runs without that contender and
 * their winner can differ from the owner's — per-viewer by construction).
 *
 * The shortest resolution window wins (`windowRank`, lower first), because it
 * is the alert where acting NOW changes the outcome. Ranks are unique by the
 * static exhaustiveness test, so the result is deterministic within one
 * snapshot; a declaration-order tie-break still backs the sort for safety.
 * Pure and synchronous: resolving the winner promotes the next-in-line in the
 * same render pass (G12) — there is no dwell state to manage here.
 */
export function arbitrateWide(alerts: tLiveAlert[]): tWideArbitration {
  const contenders = alerts.filter((alert) => alert.placement === 'wide');
  if (contenders.length === 0) return EMPTY_WIDE_ARBITRATION;

  const indexOf = new Map(ALERT_REGISTRY.map((d, i) => [d.id, i]));
  const byWindow = [...contenders].sort((a, b) => {
    const byRank =
      (a.windowRank ?? Number.MAX_SAFE_INTEGER) -
      (b.windowRank ?? Number.MAX_SAFE_INTEGER);
    if (byRank !== 0) return byRank;
    return (indexOf.get(a.id) ?? 0) - (indexOf.get(b.id) ?? 0);
  });

  return { winner: byWindow[0], waiting: byWindow.slice(1) };
}

/**
 * RET-2452 (R11) — the strip's wording of `past-due-final` while the payment
 * zone is on the page: the title and the body, with no reason line, no retry
 * line and no «Pagar ahora». The zone below already says why the charge failed
 * and carries the payment, so repeating either would show the same ask twice.
 *
 * It is applied to the WIDE WINNER only, never inside the declaration's `copy`.
 * `selectAlerts` calls `copy` once per alert and every surface reads that one
 * result, so a branch there would rewrite the bell's row on the same page too —
 * and the bell has to stay as it is today. Another alert, or this alert on
 * another page or with the flag off, comes back as the SAME object.
 */
export function withPaymentZoneStrip(options: {
  alert: tLiveAlert;
  snapshot: Pick<
    tAlertSnapshot,
    'pastDueZoneOnScreen' | 'pastDuePaymentZoneEnabled'
  >;
}): tLiveAlert {
  const { alert, snapshot } = options;
  if (alert.id !== PAST_DUE_FINAL_ALERT_ID) return alert;
  if (!snapshot.pastDueZoneOnScreen || !snapshot.pastDuePaymentZoneEnabled) {
    return alert;
  }
  return { ...alert, copy: { ...alert.copy, extraBodyKeys: [], cta: null } };
}

/**
 * Whether a CTA can actually be carried out from any Alert Center surface.
 * `intercom` and `action` always can (the support widget and the U4 action
 * seams — archive modal, availability — are app-global); `navigate` and
 * `external` need a destination. A declared target can still be missing at
 * runtime (the transfer alert's invoice URL is server-provided), which is
 * exactly the case R13c must see.
 *
 * RET-1956 — `detail` is runnable with nothing to resolve: the destination is
 * the alert's own detail modal, and its renderer is pinned by the registry
 * contract test. What a `detail` CTA DOES need is an opener on the surface
 * that renders it; the wide strip has none, so no `wide` declaration may carry
 * one (pinned in `AlertRegistry.targets.test.ts`).
 */
export function isCtaRunnable(cta: tLiveAlert['copy']['cta']): boolean {
  if (!cta) return false;
  if (cta.kind === 'intercom' || cta.kind === 'detail') return true;
  // An action CTA without an action id would render, click dead (the
  // dispatch falls through) and stay acknowledgeable — the same F4 hole,
  // one field over. The static sweep also pins this, but the runtime
  // authority must not depend on a test.
  if (cta.kind === 'action') return !!cta.action;
  return !!cta.target;
}

/**
 * Strips a CTA no surface could carry out (a `navigate`/`external` kind whose
 * target could not be resolved). A button that does nothing is worse than no
 * button. Pure alert-to-alert transform, shared by every delivery surface —
 * and by `canAcknowledge`, so acknowledgeability is judged against the CTA
 * the user actually sees (R13c), never against a stripped one.
 */
export function withActionableCta(alert: tLiveAlert): tLiveAlert {
  const { cta } = alert.copy;
  if (!cta || isCtaRunnable(cta)) return alert;
  return { ...alert, copy: { ...alert.copy, cta: null } };
}

/**
 * U12 (RET-1269) — the ordered body LINES of an alert, main sentence first.
 *
 * One definition of "which lines", four surfaces rendering them in their own
 * markup. A family whose body carries independent facts (the past-due rung's
 * sentence, why the charge failed, what Stripe does next) declares them as
 * separate keys, and a branch with nothing to say contributes no line at all —
 * so "an absent decline code renders no reason line" needs no empty string in
 * any locale.
 */
export function alertBodyKeys(alert: tLiveAlert): string[] {
  const { bodyKey, extraBodyKeys } = alert.copy;
  return [bodyKey, ...(extraBodyKeys ?? [])].filter(
    (key): key is string => typeof key === 'string' && key.length > 0,
  );
}

/**
 * F6 — the only door from a billing plan code to a display name in Alert
 * Center copy. Returns the `common/alertCenter` key for a KNOWN plan code and
 * null for anything else, so a raw billing identifier can never reach a
 * rendered sentence: an unknown code degrades to no plan name, never to
 * "PLAN_XYZ" (or `getPlanName`'s hardcoded-English "Unknown") in user-facing
 * copy. The names live per locale inside the alertCenter namespace, pinned by
 * test to `settings/SubscriptionManager.json` so the two surfaces cannot
 * drift apart.
 */
export function resolveAlertPlanNameKey(planCode: string): string | null {
  return (Object.values(ePlanCode) as string[]).includes(planCode)
    ? `plans.${planCode}`
    : null;
}

/**
 * F6 for the Lead Agent tier, same shape as the plan name above. The
 * acceptance body interpolated `suggestedTier` RAW, so the dialog read "Lead
 * Agent · GROWTH" in the body and "Lead Agent · Growth" in the offer line four
 * pixels below it (measured 2026-08-31) — the billing identifier leaking into
 * copy, which is exactly what F6 forbids.
 *
 * An unknown tier degrades to null so the copy renders without a name rather
 * than surfacing the identifier.
 */
export function resolveAlertTierNameKey(tier: string): string | null {
  return (LEAD_AGENT_SELECTABLE_TIERS as readonly string[]).includes(tier)
    ? `tier.${tier}`
    : null;
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;

/**
 * How long a red card has waited, worded the way a person says it: minutes
 * under an hour, hours under a day, then days and hours. The explainer used to
 * interpolate the raw count, so a lead idle since last week read as thousands
 * of "minutos" (RET-2018 follow-up).
 *
 * Every unit floors, because the sentence claims time that has already passed:
 * at 2 h 59 min the lead has waited two hours, not three. The day form drops a
 * zero hour ("2 días"), and a wait that is not a finite, positive number
 * degrades to zero minutes rather than putting NaN in the copy.
 *
 * The unit words come from `Intl`, not from `_one`/`_other` keys: i18next
 * resolves `br` plurals as BRETON, which serves Spanish at 2–4 and the singular
 * at 21 and 31 — every one of them a live minute, hour or day count here.
 * `toIntlLocale` hands `Intl` pt-BR instead, so the only copy left is the word
 * that joins days to hours.
 */
export function formatElapsedWait(
  minutes: number,
  options: { language: string; t: TFunction },
): string {
  const locale = toIntlLocale(options.language);
  const wait = Number.isFinite(minutes) ? Math.max(0, Math.floor(minutes)) : 0;

  if (wait < MINUTES_PER_HOUR) {
    return formatWaitUnit(wait, { locale, unit: 'minute' });
  }

  if (wait < MINUTES_PER_DAY) {
    return formatWaitUnit(Math.floor(wait / MINUTES_PER_HOUR), {
      locale,
      unit: 'hour',
    });
  }

  const days = formatWaitUnit(Math.floor(wait / MINUTES_PER_DAY), {
    locale,
    unit: 'day',
  });
  const hours = Math.floor((wait % MINUTES_PER_DAY) / MINUTES_PER_HOUR);

  if (hours === 0) return days;

  return options.t('stageThresholdExplainer.elapsed.daysAndHours', {
    days,
    hours: formatWaitUnit(hours, { locale, unit: 'hour' }),
  });
}

function formatWaitUnit(
  value: number,
  options: { locale: string; unit: 'minute' | 'hour' | 'day' },
): string {
  return new Intl.NumberFormat(options.locale, {
    style: 'unit',
    unit: options.unit,
    unitDisplay: 'long',
  }).format(value);
}

export type tEpisodeKeySelection = {
  /** Alert key → declaration id, for every episode verifiably alive now. */
  liveKeys: Map<string, string>;
  /**
   * Declaration ids whose state cannot be judged this pass (no role yet, a
   * loading source, a throwing predicate). The acknowledgment pruner must
   * leave their keys untouched — pruning on unknown would wipe persisted
   * acknowledgments on every cold load before sources hydrate.
   */
  uncertainIds: Set<string>;
};

/**
 * D6 — the episode ledger the acknowledgment pruner judges against.
 *
 * Deliberately NOT `selectAlerts`: surface scoping, exclusivity and
 * suppression are render concerns. An alert scoped off the current page — or
 * suppressed by a P0 — has not ended its episode, and with acknowledgments
 * now persisted, pruning on the surface-filtered set would turn every
 * navigation into a loss of acknowledgments (leave `home`, the quota ack is
 * gone; come back, the alert re-fires). An episode ends only when its
 * predicate verifiably turns false or the alert retires — BUG-719's rule:
 * a dismissal mutes the episode, not the alert.
 */
export function selectLiveEpisodeKeys(
  snapshot: tAlertSnapshot,
  registry: tAlertDeclaration[] = ALERT_REGISTRY,
): tEpisodeKeySelection {
  const liveKeys = new Map<string, string>();
  const uncertainIds = new Set<string>();
  const role = snapshot.role;

  for (const declaration of registry) {
    // No role means the snapshot is still assembling — judge nothing.
    if (!role) {
      uncertainIds.add(declaration.id);
      continue;
    }
    // A role outside the audience can never have acknowledged this alert, so
    // there is nothing of it to protect.
    if (!declaration.roles.includes(role)) continue;
    if (isUnresolved(declaration, snapshot)) {
      uncertainIds.add(declaration.id);
      continue;
    }
    let live = false;
    try {
      live = declaration.derive(snapshot);
    } catch {
      // A blown-up source is unknown, not an ended episode.
      uncertainIds.add(declaration.id);
      continue;
    }
    if (!live) continue;
    // KTD16 — `retired` is terminal: the alert left delivery for good, so its
    // episode is over even while the predicate holds.
    if (declaration.prominence?.(snapshot) === 'retired') continue;
    liveKeys.set(buildAlertKey(declaration, snapshot), declaration.id);
  }

  return { liveKeys, uncertainIds };
}

/**
 * R15 (U9) — declarations whose acknowledgment must survive the end of their
 * own episode, resolved by id because that is what the pruner's ledger holds.
 *
 * The default is the opposite (BUG-719: a dismissal mutes the episode, not the
 * alert) and stays the default. The exception exists for a predicate that ends
 * for reasons unrelated to the user having read it — the board's red-card
 * signal goes false on every unmount — where pruning would return the
 * acknowledgment on the way out of the page and re-show the alert on the next
 * visit. Deliberately NOT folded into `selectLiveEpisodeKeys`: that function
 * reports what is live, and lying to it would change the meaning of every
 * other reader.
 */
export function acknowledgmentOutlivesEpisode(
  declarationId: string,
  registry: tAlertDeclaration[] = ALERT_REGISTRY,
): boolean {
  return (
    registry.find((declaration) => declaration.id === declarationId)
      ?.acknowledgmentOutlivesEpisode === true
  );
}

/**
 * R13c — a P1 whose call to action resolves to nothing for this role is not
 * acknowledgeable by that role. Otherwise an admin clears an owner-only
 * revenue alert out of their own view with nothing routing it to the owner.
 *
 * "Resolves to nothing" is judged against the CTA the surface REALLY shows
 * (U4): a declared CTA whose runtime target is missing gets stripped before
 * rendering, so it must not count as actionable here either — or the user
 * could clear an alert they were never given a way to act on.
 */
export function canAcknowledge(alert: tLiveAlert, role: eUserRole): boolean {
  if (alert.priority === 'p0') return false;
  if (!alert.dismissible) return false;
  if (alert.priority === 'p1' && !isCtaRunnable(alert.copy.cta)) return false;
  // R13b — a declaration that scopes acknowledgment to a billing period cannot
  // be acknowledged while that period is unknown. The key would fall back to
  // `<alert>:<workspace>`, and once acknowledgment is persisted server-side
  // that silences the alert for every future period: exactly the permanent
  // dismissal R13b exists to prevent. Session-only today, but the key shape is
  // the documented backend contract, so the guard belongs with the contract.
  if (alert.declaration.period && alert.occurrence === null) return false;
  return alert.declaration.roles.includes(role);
}
